import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, ArrowRight, Sparkles, TrendingUp, Clock, Users, ChevronRight, ChevronLeft } from "lucide-react";
import BookCard from "../components/BookCard";
import useSEO from "../hooks/useSEO";

const CATEGORIES = [
  { name: "Theology",    icon: "✝",  desc: "Doctrine & Study",    from: "#1C0F04", to: "#5A2E0A" },
  { name: "Apologetics", icon: "🛡",  desc: "Defend the Faith",   from: "#0F1E0A", to: "#1E4A10" },
  { name: "Devotional",  icon: "🙏",  desc: "Spiritual Growth",   from: "#130F1E", to: "#2E1A5A" },
  { name: "Classics",    icon: "📜",  desc: "Historical Writings", from: "#1E0E04", to: "#5A2A06" },
  { name: "Biography",   icon: "👤",  desc: "Christian Lives",    from: "#0A0F1E", to: "#14245A" },
  { name: "Missions",    icon: "🌍",  desc: "World Christianity",  from: "#0A1E14", to: "#144A30" },
];

const AUTHOR_PALETTES = [
  ["#3D1A06","#8B4512"],["#0A2210","#1A5228"],["#180A30","#3D1A70"],
  ["#1E0804","#6B2018"],["#080F1E","#1A2A6B"],["#1A0A0A","#5A1818"],
  ["#081A1E","#0E4A5A"],["#0E0818","#2E1456"],
];

function SectionHeader({ title, subtitle, emoji, onSeeAll }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: "1.5rem" }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{ width: "3px", height: "1.5rem", background: "linear-gradient(180deg,var(--gold) 0%,var(--gold-light) 100%)", borderRadius: "2px", flexShrink: 0 }} />
          <h2 style={{
            fontFamily: "var(--font-display)", fontSize: "1.65rem", fontWeight: 700,
            color: "var(--ink)", display: "flex", alignItems: "center", gap: "8px",
          }}>
            {emoji && <span>{emoji}</span>}
            {title}
          </h2>
        </div>
        {subtitle && <p style={{ fontSize: "0.82rem", color: "var(--muted)", marginTop: "4px", paddingLeft: "13px" }}>{subtitle}</p>}
      </div>
      {onSeeAll && (
        <button onClick={onSeeAll} style={{
          display: "flex", alignItems: "center", gap: "5px",
          padding: "6px 14px", borderRadius: "var(--radius-full)",
          border: "1px solid var(--dust-2)", background: "none",
          color: "var(--muted)", fontSize: "0.78rem", fontWeight: 600,
          cursor: "pointer", transition: "all 0.18s", whiteSpace: "nowrap",
        }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--gold)"; e.currentTarget.style.color = "var(--gold)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--dust-2)"; e.currentTarget.style.color = "var(--muted)"; }}
        >
          See All <ArrowRight size={13} />
        </button>
      )}
    </div>
  );
}

function StatPill({ icon: Icon, value, label, color }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: "10px",
      background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)",
      borderRadius: "var(--radius-lg)", padding: "12px 20px",
      backdropFilter: "blur(8px)",
    }}>
      <div style={{ width: "36px", height: "36px", borderRadius: "var(--radius)", background: color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={16} color="#fff" strokeWidth={2} />
      </div>
      <div>
        <div style={{ fontFamily: "var(--font-display)", fontSize: "1.4rem", fontWeight: 700, color: "#fff", lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.08em", marginTop: "2px" }}>{label}</div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   Continue Reading — horizontal snap-scroll cards
══════════════════════════════════════════════════════ */
function ContinueReading({ books, navigate }) {
  const trackRef   = useRef(null);
  const timerRef   = useRef(null);
  const [idx, setIdx] = useState(0);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 700);

  // items per slide: 1 on mobile, 2 on desktop
  const perSlide = isDesktop ? 2 : 1;
  const total    = Math.ceil(books.length / perSlide);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 700);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const goTo = useCallback((i) => {
    const next = (i + total) % total;
    setIdx(next);
    if (trackRef.current) {
      const itemW = trackRef.current.children[0]?.offsetWidth || 0;
      const gap   = 12;
      trackRef.current.scrollTo({ left: next * perSlide * (itemW + gap), behavior: "smooth" });
    }
  }, [total, perSlide]);

  // auto-advance every 4s
  useEffect(() => {
    if (books.length <= perSlide) return;
    timerRef.current = setInterval(() => goTo(idx + 1), 4000);
    return () => clearInterval(timerRef.current);
  }, [idx, goTo, books.length, perSlide]);

  const pause  = () => clearInterval(timerRef.current);
  const resume = () => {
    if (books.length <= perSlide) return;
    timerRef.current = setInterval(() => goTo(idx + 1), 4000);
  };

  const BookRow = ({ book }) => {
    const progress  = Math.min(Math.round((parseInt(localStorage.getItem(`bookmark_${book.id}`) || 0) / (book.pages || 300)) * 100), 100);
    const from      = book.colorFrom || book.coverColor?.[0] || "#1A1108";
    const to        = book.colorTo   || book.coverColor?.[1] || "#5A2E0A";
    const pagesLeft = Math.round(((100 - progress) / 100) * (book.pages || 300));
    return (
      <div
        onClick={() => navigate(`/book/${book.id}`)}
        style={{
          display:"flex", alignItems:"center", gap:"14px",
          background:"var(--card-bg)", border:"1px solid var(--card-border)",
          borderRadius:"var(--radius-lg)", padding:"13px 15px 13px 12px",
          cursor:"pointer", transition:"border-color 0.18s, box-shadow 0.18s",
          flex: isDesktop ? "0 0 calc(50% - 6px)" : "0 0 100%",
          minWidth: 0, boxSizing:"border-box",
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor="var(--gold)"; e.currentTarget.style.boxShadow="var(--shadow-md)"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor="var(--card-border)"; e.currentTarget.style.boxShadow="none"; }}
      >
        {/* Spine thumbnail */}
        <div style={{
          width:"50px", height:"68px", borderRadius:"6px", flexShrink:0,
          background:`linear-gradient(155deg,${from},${to})`,
          position:"relative", overflow:"hidden",
          display:"flex", alignItems:"center", justifyContent:"center",
          boxShadow:"3px 4px 12px rgba(0,0,0,0.35)",
        }}>
          <div style={{ position:"absolute", left:0, top:0, bottom:0, width:"4px", background:"rgba(0,0,0,0.3)" }} />
          <div style={{ position:"absolute", top:0, left:"4px", right:0, height:"40%", background:"linear-gradient(180deg,rgba(255,255,255,0.08),transparent)" }} />
          <span style={{ fontSize:"1.25rem", marginLeft:"2px" }}>📖</span>
          <div style={{ position:"absolute", bottom:0, left:"4px", right:0, height:"3px", background:"rgba(255,255,255,0.15)" }}>
            <div style={{ height:"100%", width:`${progress}%`, background:"var(--gold-light)" }} />
          </div>
        </div>
        {/* Info */}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:"0.9rem", fontWeight:700, color:"var(--ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", marginBottom:"2px" }}>
            {book.title}
          </div>
          <div style={{ fontSize:"0.72rem", color:"var(--muted)", marginBottom:"9px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            {book.author}
          </div>
          <div style={{ height:"4px", borderRadius:"99px", background:"var(--dust-2)", overflow:"hidden", marginBottom:"5px" }}>
            <div style={{ width:`${progress}%`, height:"100%", background:"linear-gradient(90deg,var(--gold),var(--gold-2))", borderRadius:"99px" }} />
          </div>
          <div style={{ display:"flex", justifyContent:"space-between" }}>
            <span style={{ fontSize:"0.68rem", color:"var(--muted)" }}>
              {progress === 0 ? "Not started" : progress === 100 ? "✓ Finished" : `${pagesLeft} pages left`}
            </span>
            <span style={{ fontSize:"0.68rem", fontWeight:700, color:"var(--gold)" }}>{progress}%</span>
          </div>
        </div>
        <div style={{ flexShrink:0, width:"32px", height:"32px", borderRadius:"50%", background:"var(--gold-faint)", border:"1px solid var(--gold-faint-2)", display:"flex", alignItems:"center", justifyContent:"center", color:"var(--gold)" }}>
          <ChevronRight size={15} />
        </div>
      </div>
    );
  };

  const showControls = books.length > perSlide;

  return (
    <div style={{ background:"var(--cream)", padding:"1.75rem 0" }}>
      <div style={{ maxWidth:"var(--max-w)", margin:"0 auto" }}>

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"1rem", padding:"0 1.25rem" }}>
          <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
            <div style={{ width:"3px", height:"1.4rem", background:"linear-gradient(180deg,var(--gold),var(--gold-2))", borderRadius:"2px" }} />
            <h2 style={{ fontFamily:"var(--font-display)", fontSize:"1.45rem", fontWeight:700, color:"var(--ink)", margin:0 }}>
              Continue Reading
            </h2>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
            {/* Arrow controls */}
            {showControls && (
              <div style={{ display:"flex", gap:"6px" }}>
                {[{dir:-1,Icon:ChevronLeft},{dir:1,Icon:ChevronRight}].map(({dir,Icon}) => (
                  <button key={dir} onClick={() => { pause(); goTo(idx+dir); }}
                    style={{ width:"28px", height:"28px", borderRadius:"50%", border:"1px solid var(--dust-2)", background:"var(--card-bg)", color:"var(--muted)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", transition:"all 0.15s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor="var(--gold)"; e.currentTarget.style.color="var(--gold)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor="var(--dust-2)"; e.currentTarget.style.color="var(--muted)"; }}
                  ><Icon size={13} /></button>
                ))}
              </div>
            )}
            <button onClick={() => navigate("/my-library")} style={{ display:"flex", alignItems:"center", gap:"4px", background:"none", border:"none", color:"var(--gold)", fontSize:"0.8rem", fontWeight:600, cursor:"pointer", padding:0 }}>
              My Shelf <ArrowRight size={13} />
            </button>
          </div>
        </div>

        {/* Track */}
        <div style={{ overflow:"hidden", padding:"0 1.25rem" }}
          onMouseEnter={pause} onMouseLeave={resume}>
          <div ref={trackRef} style={{
            display:"flex", gap:"12px",
            overflow:"hidden",
            transition:"none",
          }}>
            {books.map(book => <BookRow key={book.id} book={book} />)}
          </div>
        </div>

        {/* Dots */}
        {showControls && (
          <div style={{ display:"flex", justifyContent:"center", gap:"6px", marginTop:"14px" }}>
            {Array.from({length:total}).map((_,i) => (
              <button key={i} onClick={() => { pause(); goTo(i); }}
                style={{ width: i===idx ? "20px" : "7px", height:"7px", borderRadius:"99px", border:"none", cursor:"pointer", padding:0, background: i===idx ? "var(--gold)" : "var(--dust-2)", transition:"all 0.3s ease" }}
              />
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

export default function Home({ books, loading }) {
  const navigate = useNavigate();

  const bestsellers = useMemo(() => books ? [...books].sort((a,b) => (b.downloadCount||0)-(a.downloadCount||0)).slice(0,8) : [], [books]);
  const newReleases  = useMemo(() => books ? [...books].sort((a,b) => (b.year||0)-(a.year||0)).slice(0,8) : [], [books]);
  const authors      = useMemo(() => books ? Array.from(new Set(books.map((b) => b.author).filter(Boolean))).slice(0,10) : [], [books]);

  const recentlyRead = useMemo(() => {
    if (!books?.length) return [];
    return books.filter((b) => localStorage.getItem(`bookmark_${b.id}`)).slice(0,6);
  }, [books]);

  const schema = useMemo(() => ({
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "WebSite", "url": "https://ebenezerbooks.com/", "name": "Ebenezer Books" },
      { "@type": "BookStore", "name": "Ebenezer Books", "url": "https://ebenezerbooks.com/", "priceRange": "$0" }
    ]
  }), []);

  useSEO({
    title: "Ebenezer Books — Classic Christian Library & Store",
    description: "An online reading library for classic Christian literature. Read, study, and shop spiritual books and theological classics.",
    keywords: "christian books, read online, christian classics, theology, christian literature",
    ogType: "website", schema,
  });

  return (
    <>
      {/* ═══════════════════════════════════════ HERO ══ */}
      <section style={{ background: "var(--hero-bg)", position: "relative", overflow: "hidden" }}>
        {/* Grid bg */}
        <div style={{ position:"absolute",inset:0, backgroundImage:"radial-gradient(rgba(255,255,255,0.025) 1px,transparent 1px)", backgroundSize:"36px 36px", pointerEvents:"none" }} />
        {/* Glow orbs */}
        <div style={{ position:"absolute",top:"-100px",left:"5%",width:"400px",height:"400px",borderRadius:"50%",background:"radial-gradient(circle,rgba(176,125,28,0.14) 0%,transparent 70%)",pointerEvents:"none" }} />
        <div style={{ position:"absolute",bottom:"-80px",right:"10%",width:"300px",height:"300px",borderRadius:"50%",background:"radial-gradient(circle,rgba(176,125,28,0.09) 0%,transparent 70%)",pointerEvents:"none" }} />

        <div style={{ maxWidth:"var(--max-w)",margin:"0 auto",padding:"5.5rem 2rem 4.5rem", position:"relative", zIndex:1 }}>
          {/* Eyebrow */}
          <div style={{ display:"inline-flex",alignItems:"center",gap:"8px",background:"rgba(176,125,28,0.15)",border:"1px solid rgba(176,125,28,0.3)",borderRadius:"var(--radius-full)",padding:"5px 16px",marginBottom:"1.75rem",animation:"fadeIn 0.5s ease" }}>
            <span style={{ width:"6px",height:"6px",borderRadius:"50%",background:"var(--gold-light)",animation:"pulse-dot 2s infinite" }} />
            <span style={{ fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",color:"var(--gold-light)" }}>
              Public Domain · Free Forever
            </span>
          </div>

          <div style={{ display:"grid",gridTemplateColumns:"1fr auto",gap:"3rem",alignItems:"center" }}>
            <div>
              <h1 style={{
                fontFamily:"var(--font-display)",
                color:"var(--hero-text)",
                fontSize:"clamp(2.6rem,5.5vw,4rem)",
                fontWeight:400,lineHeight:1.12,
                marginBottom:"1.25rem",
                animation:"fadeUp 0.6s 0.1s ease both",
                letterSpacing:"-0.01em",
              }}>
                Your Gateway to<br />
                <em style={{ color:"var(--gold-light)",fontStyle:"italic" }}>Classic Christian</em><br />
                Literature
              </h1>

              <p style={{
                color:"var(--hero-muted)", fontSize:"1.05rem",
                maxWidth:"540px", lineHeight:1.75, marginBottom:"2.25rem",
                animation:"fadeUp 0.6s 0.2s ease both",
              }}>
                Explore centuries of theological wisdom, devotional classics, and spiritual writings — read free online or order beautiful hardcopies from our store.
              </p>

              <div style={{ display:"flex",gap:"12px",flexWrap:"wrap",animation:"fadeUp 0.6s 0.3s ease both" }}>
                <button onClick={() => navigate("/search")} style={{
                  display:"flex",alignItems:"center",gap:"8px",
                  padding:"12px 28px",borderRadius:"var(--radius-full)",border:"none",
                  background:"linear-gradient(135deg,var(--gold) 0%,var(--gold-light) 100%)",
                  color:"#fff",fontSize:"0.92rem",fontWeight:700,cursor:"pointer",
                  boxShadow:"var(--shadow-gold-lg)",transition:"all 0.2s",
                }}
                  onMouseEnter={(e)=>{ e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 12px 40px rgba(176,125,28,0.55)"; }}
                  onMouseLeave={(e)=>{ e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow="var(--shadow-gold-lg)"; }}
                >
                  <BookOpen size={16} strokeWidth={2.5} />
                  Browse Library
                </button>
                <button onClick={() => navigate("/store")} style={{
                  display:"flex",alignItems:"center",gap:"8px",
                  padding:"12px 28px",borderRadius:"var(--radius-full)",
                  border:"1px solid rgba(255,255,255,0.2)",background:"rgba(255,255,255,0.07)",
                  color:"var(--hero-text)",fontSize:"0.92rem",fontWeight:500,cursor:"pointer",
                  backdropFilter:"blur(8px)",transition:"all 0.2s",
                }}
                  onMouseEnter={(e)=>{ e.currentTarget.style.background="rgba(255,255,255,0.14)"; }}
                  onMouseLeave={(e)=>{ e.currentTarget.style.background="rgba(255,255,255,0.07)"; }}
                >
                  🛍 Visit Store
                </button>
              </div>
            </div>

            {/* Stats pills — desktop */}
            {!loading && books?.length > 0 && (
              <div className="hero-stats" style={{ display:"flex",flexDirection:"column",gap:"10px",animation:"fadeUp 0.6s 0.4s ease both" }}>
                <StatPill icon={BookOpen} value={books.length} label="Books Free" color="rgba(176,125,28,0.6)" />
                <StatPill icon={Users}    value={authors.length+"+"} label="Authors"    color="rgba(58,102,69,0.6)" />
                <StatPill icon={TrendingUp} value="10K+" label="Readers"  color="rgba(37,99,235,0.5)" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ════════════ CONTINUE READING CAROUSEL ══ */}
      {recentlyRead.length > 0 && (
        <ContinueReading books={recentlyRead} navigate={navigate} />
      )}

      <main style={{ maxWidth:"var(--max-w)",margin:"0 auto",padding:"3.5rem 2rem 5rem",display:"flex",flexDirection:"column",gap:"4rem" }}>

        {/* ─── CATEGORIES ─── */}
        <section>
          <SectionHeader title="Explore by Category" subtitle="Browse our curated theological collections" />
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:"1rem" }}>
            {CATEGORIES.map((cat) => (
              <div key={cat.name}
                onClick={() => navigate(`/search?q=${encodeURIComponent(cat.name)}`)}
                style={{
                  background:`linear-gradient(150deg,${cat.from} 0%,${cat.to} 100%)`,
                  borderRadius:"var(--radius-lg)",padding:"1.5rem 1.25rem 1.25rem",
                  cursor:"pointer",position:"relative",overflow:"hidden",
                  border:"1px solid rgba(255,255,255,0.06)",
                  boxShadow:"var(--shadow)",
                  transition:"transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s",
                }}
                onMouseEnter={(e)=>{ e.currentTarget.style.transform="translateY(-4px) scale(1.02)"; e.currentTarget.style.boxShadow="var(--shadow-md)"; }}
                onMouseLeave={(e)=>{ e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow="var(--shadow)"; }}
              >
                <div style={{ position:"absolute",top:0,left:0,right:0,height:"55%",background:"linear-gradient(180deg,rgba(255,255,255,0.07) 0%,transparent 100%)",pointerEvents:"none" }} />
                <div style={{ position:"absolute",bottom:"-20px",right:"-20px",width:"70px",height:"70px",borderRadius:"50%",background:"rgba(255,255,255,0.05)",pointerEvents:"none" }} />
                <div style={{ fontSize:"1.8rem",marginBottom:"0.6rem",filter:"drop-shadow(0 2px 6px rgba(0,0,0,0.4))" }}>{cat.icon}</div>
                <div style={{ fontSize:"0.9rem",fontWeight:700,color:"#fff",textShadow:"0 1px 4px rgba(0,0,0,0.3)",marginBottom:"3px" }}>{cat.name}</div>
                <div style={{ fontSize:"0.68rem",color:"rgba(255,255,255,0.55)" }}>{cat.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ─── BESTSELLERS ─── */}
        {!loading && bestsellers.length > 0 && (
          <section>
            <SectionHeader title="Bestselling Classics" emoji="🔥" subtitle="Most downloaded from our collection" onSeeAll={() => navigate("/search")} />
            <div className="no-scrollbar" style={{ display:"flex",gap:"1.25rem",overflowX:"auto",paddingBottom:"1rem",marginBottom:"-1rem" }}>
              {bestsellers.map((book) => (
                <div key={book.id} style={{ flex:"0 0 185px" }}>
                  <BookCard book={book} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ─── STORE PROMO BANNER ─── */}
        <section>
          <div style={{
            background:"var(--store-hero)",
            borderRadius:"var(--radius-xl)",
            padding:"2.5rem",
            position:"relative",overflow:"hidden",
            border:"1px solid rgba(255,255,255,0.06)",
            display:"flex",alignItems:"center",justifyContent:"space-between",gap:"2rem",flexWrap:"wrap",
          }}>
            <div style={{ position:"absolute",top:"-60px",right:"15%",width:"200px",height:"200px",borderRadius:"50%",background:"radial-gradient(circle,rgba(74,144,96,0.18) 0%,transparent 70%)",pointerEvents:"none" }} />
            <div style={{ position:"absolute",inset:0,backgroundImage:"radial-gradient(rgba(255,255,255,0.02) 1px,transparent 1px)",backgroundSize:"28px 28px",pointerEvents:"none" }} />
            <div style={{ position:"relative",zIndex:1 }}>
              <div style={{ display:"inline-flex",alignItems:"center",gap:"6px",background:"rgba(74,144,96,0.2)",border:"1px solid rgba(74,144,96,0.3)",borderRadius:"var(--radius-full)",padding:"4px 12px",marginBottom:"1rem" }}>
                <Sparkles size={11} color="#6BCB8B" />
                <span style={{ fontSize:"0.68rem",fontWeight:700,color:"#6BCB8B",letterSpacing:"0.1em",textTransform:"uppercase" }}>Ebenezer Store</span>
              </div>
              <h3 style={{ fontFamily:"var(--font-display)",color:"#fff",fontSize:"1.8rem",fontWeight:700,lineHeight:1.2,marginBottom:"0.75rem" }}>
                Own a Beautiful<br />Hardcopy Edition
              </h3>
              <p style={{ color:"rgba(255,255,255,0.55)",fontSize:"0.88rem",lineHeight:1.6,maxWidth:"420px",marginBottom:"1.5rem" }}>
                High-quality printed editions of your favourite Christian classics. Perfect for gifting or your personal shelf.
              </p>
              <button onClick={() => navigate("/store")} style={{
                display:"inline-flex",alignItems:"center",gap:"8px",
                padding:"11px 26px",borderRadius:"var(--radius-full)",border:"none",
                background:"linear-gradient(135deg,#3A6645,#5A9A6A)",
                color:"#fff",fontSize:"0.88rem",fontWeight:700,cursor:"pointer",
                boxShadow:"0 4px 20px rgba(58,102,69,0.4)",transition:"all 0.2s",
              }}
                onMouseEnter={(e)=>{ e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 8px 30px rgba(58,102,69,0.55)"; }}
                onMouseLeave={(e)=>{ e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow="0 4px 20px rgba(58,102,69,0.4)"; }}
              >
                Shop Hardcopies <ArrowRight size={15} />
              </button>
            </div>
            <div style={{ fontSize:"6rem",opacity:0.15,position:"relative",zIndex:1,animation:"float 4s ease-in-out infinite" }}>📚</div>
          </div>
        </section>

        {/* ─── AUTHORS ─── */}
        {!loading && authors.length > 0 && (
          <section>
            <SectionHeader title="Browse by Author" emoji="✍️" subtitle="Explore works from history's greatest Christian writers" />
            <div className="no-scrollbar" style={{ display:"flex",gap:"1.5rem",overflowX:"auto",paddingBottom:"0.5rem" }}>
              {authors.map((author, idx) => {
                const initials = author.split(" ").map((w)=>w[0]).join("").toUpperCase().slice(0,2);
                const [f,t] = AUTHOR_PALETTES[idx % AUTHOR_PALETTES.length];
                return (
                  <div key={author}
                    onClick={() => navigate(`/search?q=${encodeURIComponent(author)}`)}
                    style={{ flex:"0 0 84px",display:"flex",flexDirection:"column",alignItems:"center",cursor:"pointer",textAlign:"center" }}
                  >
                    <div style={{
                      width:"62px",height:"62px",borderRadius:"50%",
                      background:`linear-gradient(145deg,${f},${t})`,
                      border:"2px solid rgba(255,255,255,0.10)",
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:"1rem",fontWeight:700,color:"rgba(255,255,255,0.9)",
                      boxShadow:"var(--shadow)",marginBottom:"0.5rem",
                      transition:"transform 0.25s cubic-bezier(0.34,1.56,0.64,1),box-shadow 0.25s",
                    }}
                      onMouseEnter={(e)=>{ e.currentTarget.style.transform="scale(1.1)"; e.currentTarget.style.boxShadow="var(--shadow-gold)"; }}
                      onMouseLeave={(e)=>{ e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow="var(--shadow)"; }}
                    >{initials || "?"}</div>
                    <span style={{ fontSize:"0.72rem",fontWeight:600,color:"var(--ink)",lineHeight:1.25,overflow:"hidden",textOverflow:"ellipsis",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",width:"76px" }}>
                      {author}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ─── NEW ADDITIONS ─── */}
        {!loading && newReleases.length > 0 && (
          <section>
            <SectionHeader title="Newest Additions" emoji="✨" subtitle="Recently added to the library" onSeeAll={() => navigate("/search")} />
            <div className="no-scrollbar" style={{ display:"flex",gap:"1.25rem",overflowX:"auto",paddingBottom:"1rem",marginBottom:"-1rem" }}>
              {newReleases.map((book) => (
                <div key={book.id} style={{ flex:"0 0 185px" }}>
                  <BookCard book={book} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ─── SCRIPTURE QUOTE ─── */}
        <section>
          <div style={{
            background:"var(--card-bg)",border:"1px solid var(--card-border)",
            borderRadius:"var(--radius-xl)",padding:"3rem 2.5rem",
            textAlign:"center",position:"relative",overflow:"hidden",
            boxShadow:"var(--shadow)",
          }}>
            <div style={{ position:"absolute",top:"-40px",left:"50%",transform:"translateX(-50%)",fontSize:"10rem",opacity:0.03,lineHeight:1,pointerEvents:"none",userSelect:"none" }}>"</div>
            <div style={{ position:"relative",zIndex:1 }}>
              <div style={{ fontSize:"3rem",marginBottom:"1rem",opacity:0.7 }}>📜</div>
              <blockquote style={{
                fontFamily:"var(--font-display)",fontSize:"clamp(1.2rem,2.5vw,1.6rem)",
                fontStyle:"italic",color:"var(--ink)",lineHeight:1.65,
                maxWidth:"680px",margin:"0 auto 1.25rem",fontWeight:400,
              }}>
                "Thy word is a lamp unto my feet, and a light unto my path."
              </blockquote>
              <cite style={{ fontSize:"0.8rem",color:"var(--muted)",fontStyle:"normal",fontWeight:600,letterSpacing:"0.06em",textTransform:"uppercase" }}>
                Psalm 119:105
              </cite>
            </div>
          </div>
        </section>

      </main>

      <style>{`
        @media (max-width:768px) {
          .hero-stats { display:none !important; }
        }
      `}</style>
    </>
  );
}
